import React from 'react';
import { cn } from '@gu-corp/gu-ui';

/**
 * Tier seal icon — "Gem Cut" (design proposal D).
 *
 * A gemstone cross-section that gains facet lines as the tier rises:
 *   White    — outline only (rough stone)
 *   Bronze   — girdle line
 *   Silver   — center axis
 *   Gold     — crown facets + outer ring
 *   Platinum — full cut, double ring, culet dot
 */

export type GemTier = 'white' | 'bronze' | 'silver' | 'gold' | 'platinum';

const TIER_INDEX: Record<GemTier, number> = {
  white: 0,
  bronze: 1,
  silver: 2,
  gold: 3,
  platinum: 4,
};

/** Tier colors tuned to stay legible on both light and dark surfaces. */
export const TIER_GEM_COLOR_CLASS: Record<GemTier, string> = {
  white: 'text-[#8a827b] dark:text-[#a8a09a]',
  bronze: 'text-[#c4934a]',
  silver: 'text-[#8fa9bb] dark:text-[#9fb8c8]',
  gold: 'text-[#d4a83a]',
  platinum: 'text-[#a39c94] dark:text-[#dfd9d3]',
};

// White stays the quietest mark but must remain visible on light surfaces
const OPACITY = [0.5, 0.62, 0.8, 0.93, 1];
const STROKE = [1, 1.5, 1.8, 2, 2];
const SCALE = [0.82, 0.88, 1, 1, 1];

type Pt = [number, number];

function normalizeTier(tier: string): GemTier {
  const key = tier.toLowerCase();
  if (key in TIER_INDEX) return key as GemTier;
  // JocClubTier.NONE and anything unknown render as the rough stone
  return 'white';
}

interface TierGemIconProps {
  /** Tier name, case-insensitive ('White', 'bronze', JocClubTier values...) */
  tier: string;
  className?: string;
  /** Apply the built-in tier color (default true). Set false to inherit currentColor. */
  withColor?: boolean;
}

export const TierGemIcon: React.FC<TierGemIconProps> = ({ tier, className, withColor = true }) => {
  const gemTier = normalizeTier(tier);
  const t = TIER_INDEX[gemTier];

  const op = OPACITY[t];
  const sw = STROKE[t];
  const sc = SCALE[t];
  const iw = Math.max(0.7, sw - 0.5);

  const TL: Pt = [40 - 10 * sc, 40 - 14 * sc];
  const TR: Pt = [40 + 10 * sc, 40 - 14 * sc];
  const GL: Pt = [40 - 18 * sc, 40];
  const GR: Pt = [40 + 18 * sc, 40];
  const TIP: Pt = [40, 40 + 16 * sc];
  const TC: Pt = [40, 40 - 14 * sc];
  const GC: Pt = [40, 40];

  const pt = ([x, y]: Pt) => `${x.toFixed(1)},${y.toFixed(1)}`;
  const line = (a: Pt, b: Pt, key: string) => (
    <line
      key={key}
      x1={a[0].toFixed(1)}
      y1={a[1].toFixed(1)}
      x2={b[0].toFixed(1)}
      y2={b[1].toFixed(1)}
      stroke="currentColor"
      strokeWidth={iw}
      opacity={op}
    />
  );

  return (
    <svg
      viewBox="0 0 80 80"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      className={cn(withColor && TIER_GEM_COLOR_CLASS[gemTier], className)}
    >
      {/* Outer ring(s) — Gold+ */}
      {t >= 3 && (
        <circle
          cx="40"
          cy="40"
          r="33"
          stroke="currentColor"
          strokeWidth={t === 4 ? 1.5 : 0.9}
          opacity={t === 4 ? 0.55 : 0.28}
          fill="none"
        />
      )}
      {t === 4 && (
        <circle
          cx="40"
          cy="40"
          r="27"
          stroke="currentColor"
          strokeWidth={0.6}
          opacity={0.2}
          fill="none"
        />
      )}

      {/* Diamond outline */}
      <polygon
        points={`${pt(TL)} ${pt(TR)} ${pt(GR)} ${pt(TIP)} ${pt(GL)}`}
        stroke="currentColor"
        strokeWidth={sw}
        opacity={op}
        strokeLinejoin="miter"
        fill="none"
      />

      {/* Girdle horizontal — Bronze+ */}
      {t >= 1 && line(GL, GR, 'girdle')}

      {/* Center vertical (table → tip) — Silver+ */}
      {t >= 2 && line(TC, TIP, 'axis')}

      {/* Crown diagonals — Gold+ */}
      {t >= 3 && line(TL, GC, 'crown-l')}
      {t >= 3 && line(TR, GC, 'crown-r')}

      {/* Pavilion diagonals + culet dot — Platinum */}
      {t === 4 && line(TL, TIP, 'pav-l')}
      {t === 4 && line(TR, TIP, 'pav-r')}
      {t === 4 && (
        <circle cx="40" cy={TIP[1].toFixed(1)} r="2.5" fill="currentColor" opacity={0.88} />
      )}
    </svg>
  );
};

export default TierGemIcon;
